import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Button,
    IconButton,
    Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';

interface DeleteTeamModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    teamName: string;
    loading?: boolean;
}

export const DeleteTeamModal: React.FC<DeleteTeamModalProps> = ({
    open,
    onClose,
    onConfirm,
    teamName,
    loading,
}) => {
    const [confirmText, setConfirmText] = useState('');

    const handleClose = () => {
        setConfirmText('');
        onClose();
    };

    const handleDelete = () => {
        if (confirmText.toLowerCase() === teamName.toLowerCase()) {
            onConfirm();
            setConfirmText('');
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ color: GT_COLORS.red, fontSize: '20px' }} />
                    <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Delete Team</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" sx={{ color: GT_COLORS.textFaint }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: '14px', color: GT_COLORS.textMuted, mb: 2 }}>
                    This will permanently delete <strong>{teamName}</strong> and remove all member
                    assignments. Team leaders will lose their role. This action cannot be undone.
                </Typography>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: GT_COLORS.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '.5px',
                        mb: '4px',
                    }}
                >
                    Type "{teamName}" to confirm
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={teamName}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        color: GT_COLORS.textMuted,
                        borderColor: GT_COLORS.borderInput,
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '8px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleDelete}
                    disabled={confirmText.toLowerCase() !== teamName.toLowerCase() || loading}
                    variant="contained"
                    sx={{
                        backgroundColor: GT_COLORS.red,
                        textTransform: 'none',
                        fontSize: '14px',
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: '#B91C1C' },
                    }}
                >
                    Delete Team
                </Button>
            </DialogActions>
        </Dialog>
    );
};
